import { InfiniteData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { useRef } from 'react';
import { api } from '@/modules/common/api/index.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { invalidateQueriesTransactions } from '@/modules/issuer-dashboard/queries/activities.tsx';
import {
  CreatePaymentRequest,
  GetPaymentByIdExtendedResponse,
  GetPaymentByIdV1SrPaymentsIdGetParams,
  GetPaymentListResponse,
  GetPaymentsListV1SrPaymentsGetParams,
  PaymentSetCompletedStatusRequest,
  PaymentSetRejectedStatusRequest,
} from 'api';

export const CURRENT_ISSUER_PAYMENTS_QUERY_KEY = 'vestra/issuer-current-payments';

export const usePaymentByIdQuery = (
  payload: GetPaymentByIdV1SrPaymentsIdGetParams,
  disabled = false,
  register_id?: number,
) => {
  return useQuery<GetPaymentByIdExtendedResponse>({
    queryKey: [CURRENT_ISSUER_PAYMENTS_QUERY_KEY, JSON.stringify(payload), register_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getPaymentByIdV1SrPaymentsIdGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const usePaymentsListQuery = (
  payload: GetPaymentsListV1SrPaymentsGetParams,
  disabled = false,
  register_id?: number,
) => {
  const { page, page_size, ...data } = payload;

  const previousDataRef = useRef<InfiniteData<GetPaymentListResponse> | null>(null);

  return useInfiniteQuery<GetPaymentListResponse>({
    queryKey: [CURRENT_ISSUER_PAYMENTS_QUERY_KEY, page_size, register_id, JSON.stringify(data)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getPaymentsListV1SrPaymentsGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
    refetchInterval: 1000 * 60, //1min,
    onSuccess: async data => {
      const previousData = previousDataRef.current;

      if (previousData) {
        const hasStatusChanged = data?.pages.some((newPage, pageIndex) =>
          newPage.data.some((newItem, itemIndex) => {
            const prevItem = previousData?.pages[pageIndex]?.data?.[itemIndex];
            return prevItem && newItem.status !== prevItem.status;
          }),
        );
        if (hasStatusChanged) {
          await invalidateQueriesTransactions({ updateDividend: false });
        }
      }

      previousDataRef.current = data;
    },
  });
};

export const usePaymentArchiveByIdMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.archivePaymentV1SrArchivingPaymentsIdPost({ id }),
        {
          loading: 'Payment archiving...',
          success: 'Payment archived!',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions();
    },
  });
};

export const useCreatePaymentMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreatePaymentRequest) => {
      const response = await withErrorHandling(api.v1.createPaymentV1SrPaymentsPost(payload), {
        loading: 'Creating payment...',
        success: 'Payment created',
      });

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({ updateDividend: false });
    },
  });
};

export const usePaymentSetCompletedMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: PaymentSetCompletedStatusRequest) => {
      const response = await withErrorHandling(
        api.v1.paymentSetCompletedV1SrPaymentsStatusCompletedPatch(payload),
        {
          loading: 'Updating payment...',
          success: 'Payment updated',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({ updateDividend: false });
    },
  });
};

export const usePaymentSetRejectedMutation = () => {
  return useMutation({
    mutationFn: async (payload: PaymentSetRejectedStatusRequest) => {
      const response = await withErrorHandling(
        api.v1.paymentSetRejectedV1SrPaymentsStatusRejectedPatch(payload),
        {
          loading: 'Updating payment...',
          success: 'Payment updated',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await invalidateQueriesTransactions({ updateDividend: false });
    },
  });
};
